import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  BarChart3, Download, FileText, Loader2, ArrowLeft, Shield,
  Users, Activity, Clock, CheckCircle2, AlertTriangle, Crown, Stethoscope,
} from 'lucide-react';

const FOREST = '#2C4A3B';
const INDIGO = '#4F46E5';
const AMBER = '#D97706';
const CHARCOAL = '#1C1917';

// Keyframes (injected once per mount) — powers the chart animations below.
const CHART_ANIMATIONS_CSS = `
@keyframes plx-bar-grow { from { transform: scaleY(0); } to { transform: scaleY(1); } }
@keyframes plx-fade-up  { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
@keyframes plx-hbar-grow {
  from { width: 0; }
  to   { width: var(--plx-target-w); }
}
.plx-bar-anim       { transform-box: fill-box; transform-origin: bottom; animation: plx-bar-grow 0.7s cubic-bezier(.2,.7,.3,1) both; }
.plx-card-anim      { animation: plx-fade-up 0.5s cubic-bezier(.2,.7,.3,1) both; }
.plx-hbar-fill      { width: 0; animation: plx-hbar-grow 0.9s cubic-bezier(.2,.7,.3,1) both; }
@media (prefers-reduced-motion: reduce) {
  .plx-bar-anim, .plx-card-anim { animation: none !important; }
  .plx-hbar-fill { animation: none !important; width: var(--plx-target-w) !important; }
}
`;

// Count-up hook — animates a number from 0 → target over `duration`.
function useCountUp(target, duration = 900) {
  const [value, setValue] = useState(0);
  const frameRef = useRef();
  useEffect(() => {
    if (target == null || Number.isNaN(target)) { setValue(0); return; }
    const end = Number(target);
    if (!Number.isFinite(end)) { setValue(target); return; }
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setValue(end);
      return;
    }
    const start = performance.now();
    const tick = (now) => {
      const pct = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - pct, 3); // easeOutCubic
      setValue(end * eased);
      if (pct < 1) frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target, duration]);
  return value;
}

function AnimatedNumber({ value, decimals = 0 }) {
  const animated = useCountUp(value);
  if (value == null || Number.isNaN(value)) return '—';
  const n = Math.round(animated * Math.pow(10, decimals)) / Math.pow(10, decimals);
  if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: decimals });
  return n.toFixed(decimals).replace(/\.0+$/, '');
}

const fmtNumber = (n) => {
  if (n == null || Number.isNaN(n)) return '—';
  if (n >= 1000) return n.toLocaleString();
  return String(n);
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
};

export default function AnalyticsPage({ user, token }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isEnterprise, setIsEnterprise] = useState(null); // null=unknown, true/false
  const [days, setDays] = useState(30);
  const [usage, setUsage] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [exporting, setExporting] = useState(null); // 'csv' | 'pdf' | null

  const authHeader = useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  // Inject the one-off keyframes sheet so all chart children can use the classes.
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-plx-charts', '');
    styleEl.textContent = CHART_ANIMATIONS_CSS;
    document.head.appendChild(styleEl);
    return () => { styleEl.remove(); };
  }, []);

  // 1. Check entitlement
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/subscription/status`, authHeader);
        setIsEnterprise(!!data?.is_enterprise);
      } catch {
        setIsEnterprise(false);
      }
    })();
  }, [token, authHeader]);

  // 2. Load data whenever enterprise is confirmed or window changes
  useEffect(() => {
    if (isEnterprise !== true) return;
    setLoading(true);
    (async () => {
      try {
        const [u, c] = await Promise.all([
          axios.get(`${API}/analytics/usage?days=${days}`, authHeader),
          axios.get(`${API}/analytics/compliance`, authHeader),
        ]);
        setUsage(u.data);
        setCompliance(c.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not load analytics');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEnterprise, days, authHeader]);

  const handleExport = async (kind) => {
    setExporting(kind);
    try {
      const res = await axios.get(
        `${API}/analytics/export.${kind}?days=${days}`,
        { ...authHeader, responseType: 'blob' },
      );
      const blob = new Blob([res.data], {
        type: kind === 'csv' ? 'text/csv' : 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `plexura_analytics_${stamp}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Report downloaded (${kind.toUpperCase()})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  // ─── Gating / loading states ──────────────────────────────
  if (isEnterprise === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  if (isEnterprise === false) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F7F5F0' }}>
        <Card
          className="max-w-md w-full p-8 text-center rounded-3xl border border-indigo-200"
          style={{ background: 'linear-gradient(135deg,#EEF2FF,#F5F3FF)' }}
          data-testid="analytics-enterprise-gate"
        >
          <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center bg-indigo-600">
            <BarChart3 className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: CHARCOAL }}>
            Usage analytics & compliance reports
          </h1>
          <p className="text-sm text-stone-600 mb-5">
            Practice-wide usage trends, compliance posture, and exportable reports are part of the
            Enterprise plan.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              onClick={() => navigate('/contact')}
              className="h-11 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
              data-testid="analytics-gate-sales-cta"
            >
              Talk to Sales
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="h-11 rounded-xl"
            >
              Back to dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F7F5F0' }}>
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="rounded-xl shrink-0 px-3 sm:px-4"
              data-testid="analytics-back-btn"
            >
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2" style={{ color: CHARCOAL, fontFamily: 'Manrope, sans-serif' }}>
                <BarChart3 className="w-5 h-5" style={{ color: INDIGO }} />
                Analytics & Compliance
                <span className="inline-flex items-center gap-1 bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full">
                  Enterprise
                </span>
              </h1>
              <p className="text-xs text-stone-500 truncate">
                {usage
                  ? `${usage.scope_provider_ids.length} provider${usage.scope_provider_ids.length !== 1 ? 's' : ''} in scope · last ${usage.window_days} days`
                  : 'Loading…'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-xl border border-stone-200 bg-white p-1" data-testid="analytics-range-selector">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  data-testid={`analytics-range-${d}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${days === d ? 'text-white' : 'text-stone-600 hover:text-stone-900'}`}
                  style={days === d ? { background: INDIGO } : {}}
                >
                  {d}d
                </button>
              ))}
            </div>
            <Button
              onClick={() => handleExport('csv')}
              disabled={exporting !== null || loading}
              variant="outline"
              className="h-9 rounded-xl"
              data-testid="analytics-export-csv"
            >
              {exporting === 'csv'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <><Download className="w-4 h-4 mr-1" /> CSV</>}
            </Button>
            <Button
              onClick={() => handleExport('pdf')}
              disabled={exporting !== null || loading}
              className="h-9 rounded-xl text-white font-semibold"
              style={{ background: INDIGO }}
              data-testid="analytics-export-pdf"
            >
              {exporting === 'pdf'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <><FileText className="w-4 h-4 mr-1" /> PDF</>}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-8">
        {loading || !usage || !compliance ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <>
            <UsageKPIs usage={usage} />
            <DailyTrend trend={usage.daily_trend} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SessionTypes items={usage.session_types} />
              <DurationBuckets items={usage.duration_buckets} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ProviderTable rows={usage.per_provider} />
              <CptDistribution items={usage.cpt_distribution} />
            </div>

            <ComplianceSection compliance={compliance} />
          </>
        )}
      </main>
    </div>
  );
}

// ─── Usage KPIs ────────────────────────────────────────────
function UsageKPIs({ usage }) {
  const t = usage.totals;
  const kpis = [
    { label: 'Sessions', value: t.sessions, icon: Activity, accent: INDIGO, testid: 'kpi-sessions' },
    { label: 'Minutes recorded', value: t.minutes_recorded, decimals: 1, icon: Clock, accent: FOREST, testid: 'kpi-minutes' },
    { label: 'Patients seen', value: t.patients_active, icon: Users, accent: AMBER, testid: 'kpi-patients' },
    { label: 'Notes generated', value: t.notes_total, icon: FileText, accent: INDIGO, testid: 'kpi-notes' },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
      {kpis.map((k, i) => {
        const Icon = k.icon;
        return (
          <Card
            key={k.label}
            className="p-5 rounded-2xl border border-stone-200 bg-white plx-card-anim"
            style={{ animationDelay: `${i * 60}ms` }}
            data-testid={k.testid}
          >
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: k.accent }}>
              <Icon className="w-3.5 h-3.5" />
              {k.label}
            </div>
            <p className="text-3xl font-bold tabular-nums" style={{ color: CHARCOAL, fontFamily: 'Manrope, sans-serif' }}>
              <AnimatedNumber value={k.value} decimals={k.decimals || 0} />
            </p>
          </Card>
        );
      })}
      <Card
        className="p-5 rounded-2xl border border-stone-200 bg-white lg:col-span-4 plx-card-anim"
        style={{ animationDelay: '260ms' }}
        data-testid="note-breakdown"
      >
        <div className="flex flex-wrap gap-6 justify-between items-center">
          <div>
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Notes by type</p>
            <div className="flex items-center gap-6 text-sm">
              <span><strong><AnimatedNumber value={t.notes_soap} /></strong> SOAP</span>
              <span><strong><AnimatedNumber value={t.notes_psychotherapy} /></strong> Psychotherapy</span>
              <span><strong><AnimatedNumber value={t.notes_diagnosis} /></strong> Diagnoses</span>
            </div>
          </div>
          <div className="text-sm text-stone-500">
            Avg session: <strong style={{ color: CHARCOAL }}><AnimatedNumber value={t.avg_session_minutes} decimals={1} /></strong> min ·
            <span className="ml-2">Lifetime patients: <strong style={{ color: CHARCOAL }}><AnimatedNumber value={t.patients_lifetime} /></strong></span>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ─── Daily Trend (inline SVG bars) ─────────────────────────
function DailyTrend({ trend }) {
  const max = Math.max(1, ...trend.map((r) => r.sessions));
  const W = 760;
  const H = 140;
  const barGap = 2;
  const barW = Math.max(3, (W - barGap * (trend.length - 1)) / trend.length);
  // Stagger each bar's animation start by a few ms — gives the "wave growing
  // left→right" effect. Capped so 90-day views still finish in ~1s total.
  const perBarDelay = Math.min(18, 900 / Math.max(1, trend.length));
  // Remount key so switching the date range replays the animation cleanly.
  const animKey = `${trend.length}-${trend[0]?.date}-${trend[trend.length - 1]?.date}`;

  return (
    <Card className="p-5 rounded-2xl border border-stone-200 bg-white plx-card-anim" data-testid="daily-trend">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-bold" style={{ color: CHARCOAL }}>Sessions over time</h3>
          <p className="text-xs text-stone-500">Daily count · peaks at {max}</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <svg key={animKey} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-32 min-w-[540px]">
          {trend.map((r, i) => {
            const h = (r.sessions / max) * (H - 20);
            const x = i * (barW + barGap);
            const y = H - h - 4;
            return (
              <g key={r.date}>
                <rect
                  className="plx-bar-anim"
                  style={{ animationDelay: `${i * perBarDelay}ms` }}
                  x={x} y={y} width={barW} height={h}
                  rx={2} ry={2}
                  fill={INDIGO}
                  opacity={r.sessions ? 0.85 : 0.12}
                >
                  <title>{`${r.date}: ${r.sessions}`}</title>
                </rect>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="flex justify-between text-[10px] text-stone-400 mt-1 px-1">
        <span>{trend[0]?.date}</span>
        <span>{trend[trend.length - 1]?.date}</span>
      </div>
    </Card>
  );
}

// ─── Horizontal bar list helpers ───────────────────────────
function HBarList({ title, items, labelKey, accent, testid }) {
  const max = Math.max(1, ...items.map((r) => r.count));
  // Remount on data swap so width-grow animation replays from 0.
  const animKey = items.map((r) => `${r[labelKey]}:${r.count}`).join('|');
  return (
    <Card className="p-5 rounded-2xl border border-stone-200 bg-white plx-card-anim" data-testid={testid}>
      <h3 className="text-base font-bold mb-4" style={{ color: CHARCOAL }}>{title}</h3>
      {items.length === 0 ? (
        <p className="text-sm text-stone-500 py-6 text-center">No data in this window.</p>
      ) : (
        <ul key={animKey} className="space-y-2.5">
          {items.map((r, i) => {
            const label = r[labelKey] || '—';
            const pct = (r.count / max) * 100;
            return (
              <li key={label} className="plx-card-anim" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="flex items-baseline justify-between text-xs mb-1">
                  <span className="font-medium text-stone-700 truncate">{label}</span>
                  <span className="tabular-nums font-semibold" style={{ color: CHARCOAL }}>
                    <AnimatedNumber value={r.count} />
                  </span>
                </div>
                <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full plx-hbar-fill"
                    style={{
                      '--plx-target-w': `${pct}%`,
                      background: accent,
                      animationDelay: `${150 + i * 50}ms`,
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function SessionTypes({ items }) {
  return <HBarList title="Session types" items={items} labelKey="type" accent={FOREST} testid="session-types" />;
}

function DurationBuckets({ items }) {
  const labelled = items.map((b) => ({ label: `${b.bucket} min`, count: b.count }));
  return <HBarList title="Duration distribution" items={labelled} labelKey="label" accent={INDIGO} testid="duration-buckets" />;
}

function CptDistribution({ items }) {
  return <HBarList title="CPT codes billed (top 10)" items={items} labelKey="code" accent={AMBER} testid="cpt-distribution" />;
}

// ─── Per-provider table ────────────────────────────────────
function ProviderTable({ rows }) {
  return (
    <Card className="p-5 rounded-2xl border border-stone-200 bg-white" data-testid="provider-table">
      <h3 className="text-base font-bold mb-4" style={{ color: CHARCOAL }}>Per-provider breakdown</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-stone-500 py-6 text-center">No providers in scope.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] font-semibold uppercase tracking-wide text-stone-500 border-b border-stone-200">
                <th className="text-left py-2">Provider</th>
                <th className="text-right py-2">Sessions</th>
                <th className="text-right py-2">Minutes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.provider_id} className="border-b border-stone-100 last:border-0">
                  <td className="py-2.5 text-stone-800">{r.name}</td>
                  <td className="py-2.5 text-right tabular-nums font-semibold" style={{ color: CHARCOAL }}>{r.sessions}</td>
                  <td className="py-2.5 text-right tabular-nums text-stone-600">{r.minutes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ─── Compliance section ────────────────────────────────────
function ComplianceSection({ compliance }) {
  const { consent, providers, audit, review, security } = compliance;

  return (
    <div className="space-y-6" data-testid="compliance-section">
      <div className="flex items-center gap-2">
        <Shield className="w-5 h-5" style={{ color: FOREST }} />
        <h2 className="text-xl font-bold" style={{ color: CHARCOAL, fontFamily: 'Manrope, sans-serif' }}>
          Compliance posture
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <ComplianceStat
          icon={CheckCircle2}
          accent={FOREST}
          label="Consent rate"
          value={`${consent.rate_pct}%`}
          sub={`${consent.patients_with_consent} / ${consent.total_patients} patients`}
          testid="comp-consent"
        />
        <ComplianceStat
          icon={Users}
          accent={INDIGO}
          label="Active providers"
          value={String(providers.active)}
          sub={`${providers.pending_invites} pending · ${providers.revoked_invites} revoked`}
          testid="comp-providers"
        />
        <ComplianceStat
          icon={Activity}
          accent={AMBER}
          label="Audit events · 30d"
          value={fmtNumber(audit.events_30d)}
          sub={`${fmtNumber(audit.phi_access_events_30d)} PHI access events`}
          testid="comp-audit"
        />
        <ComplianceStat
          icon={Stethoscope}
          accent={FOREST}
          label="Transcript review"
          value={`${review.approval_rate_pct}%`}
          sub={`${review.transcripts_reviewed} / ${review.transcripts_total} reviewed`}
          testid="comp-review"
        />
      </div>

      <Card className="p-5 rounded-2xl border border-stone-200 bg-white" data-testid="security-posture">
        <h3 className="text-base font-bold mb-4" style={{ color: CHARCOAL }}>Security posture</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { ok: security.encryption_at_rest, label: 'Encryption at rest' },
            { ok: security.encryption_in_transit, label: 'Encryption in transit' },
            { ok: security.hipaa_compliant, label: 'HIPAA-compliant infra' },
          ].map((r) => (
            <div key={r.label} className="flex items-center gap-2 bg-stone-50 rounded-xl p-3">
              {r.ok
                ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                : <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />}
              <span className="text-sm font-medium" style={{ color: CHARCOAL }}>{r.label}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 rounded-2xl border border-stone-200 bg-white" data-testid="provider-roster-table">
        <h3 className="text-base font-bold mb-4" style={{ color: CHARCOAL }}>Provider roster</h3>
        {providers.roster.length === 0 ? (
          <p className="text-sm text-stone-500 py-4">No providers in scope.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] font-semibold uppercase tracking-wide text-stone-500 border-b border-stone-200">
                  <th className="text-left py-2">Provider</th>
                  <th className="text-left py-2">Role</th>
                  <th className="text-left py-2">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {providers.roster.map((r) => (
                  <tr key={r.provider_id} className="border-b border-stone-100 last:border-0">
                    <td className="py-2.5">
                      <span className="font-medium" style={{ color: CHARCOAL }}>{r.name}</span>
                    </td>
                    <td className="py-2.5">
                      {r.role === 'owner' ? (
                        <span className="inline-flex items-center gap-1 text-indigo-700 text-xs font-semibold">
                          <Crown className="w-3 h-3" /> Owner
                        </span>
                      ) : (
                        <span className="text-stone-600 text-xs capitalize">{r.role}</span>
                      )}
                    </td>
                    <td className="py-2.5 text-stone-600">{fmtDate(r.last_activity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function ComplianceStat({ icon: Icon, accent, label, value, sub, testid }) {
  return (
    <Card className="p-5 rounded-2xl border border-stone-200 bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: accent }}>
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <p className="text-2xl font-bold" style={{ color: CHARCOAL }}>{value}</p>
      <p className="text-xs text-stone-500 mt-1">{sub}</p>
    </Card>
  );
}
